import {
  api,
  CreateOrUpdateExaminerDto,
  ExaminerCertificationStatus,
  ExaminerCertificationStatusWithDates,
  ExaminerDetails,
  UpdateExaminerParams,
  PagedResultOfExaminerDto,
  Trainer,
  GetDisciplinaryReviewDateParams,
  ExaminerDto,
} from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';
import { TableState } from 'types';
import { ExaminerListFilterType } from './examinerTypes';
import {
  getBcCentresIds,
  getIdpCentresIds,
  getPaginationParameters,
  getQueryStringParameters,
} from 'utils';

type ExaminerListParams = TableState<ExaminerDto, ExaminerListFilterType>;

const mapExaminerListParams = (params: ExaminerListParams) => {
  const { filters } = params;

  return {
    ...getPaginationParameters(params),
    filter: {
      searchText: filters.searchText || undefined,
      speakingStatuses: filters.speakingStatuses,
      writingStatuses: filters.writingStatuses,
      onlineSpeakingStatuses: filters.onlineSpeakingStatuses,
      lifeSkillsStatuses: filters.lifeSkillsStatuses,
      centres: getBcCentresIds(params.filters.centresIds),
      idpCentres: getIdpCentresIds(params.filters.centresIds),
      countryIds: filters.countriesIds,
      regionIds: filters.regionsIds,
      countryOfResidenceId: filters.countryOfResidenceId,
      centreType: filters.centreType,
      showNomads: filters.showNomads,
      listType: filters.listType,
      detailedCertificationType: filters.detailedCertificationType,
    },
  };
};

const examinersApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    getExaminerListRecords: builder.query<PagedResultOfExaminerDto, ExaminerListParams>({
      query: (params) => {
        const input = getQueryStringParameters(mapExaminerListParams(params));
        return api.examiner.getExaminersV2(input);
      },
    }),

    searchExaminers: builder.query<PagedResultOfExaminerDto, string>({
      query: (searchText) => api.examiner.getExaminersV2({ 'filter.searchText': searchText }),
    }),

    getExaminer: builder.query<ExaminerDetails, number>({
      query: (examinerId) => api.examiner.getExaminerDetails({ examinerId }),
      providesTags: ['Examiner'],
    }),

    createExaminer: builder.mutation<ExaminerDetails, CreateOrUpdateExaminerDto>({
      query: (data) => api.examiner.createExaminer(data),
      invalidatesTags: ['Examiner', 'ExaminerStatus'],
    }),

    updateExaminer: builder.mutation<
      ExaminerDetails,
      { examinerId: UpdateExaminerParams['examinerId']; data: CreateOrUpdateExaminerDto }
    >({
      query: ({ examinerId, data }) => api.examiner.updateExaminer({ examinerId }, data),
      invalidatesTags: ['Examiner', 'ExaminerStatus'],
    }),

    checkDuplicateExaminers: builder.query<
      PagedResultOfExaminerDto,
      { firstName: string; lastName: string }
    >({
      query: ({ firstName, lastName }) =>
        api.examiner.getExaminersV2({
          'filter.firstName': firstName,
          'filter.lastName': lastName,
          'filter.showNomads': true,
        }),
    }),

    getExaminerStatus: builder.query<ExaminerCertificationStatusWithDates, number>({
      query: (examinerId) => api.examinerCertificationStatus.getExaminerStatus({ examinerId }),
      providesTags: ['ExaminerStatus'],
      structuralSharing: false,
    }),

    getTrainers: builder.query<Trainer[], void>({
      query: () => api.examiner.getTrainers(),
    }),

    hasRequiredTrainingForOdp: builder.query<boolean, number>({
      query: (examinerId) => api.odpAssessment.hasRequiredTraining({ examinerId }),
      providesTags: ['Training'],
    }),

    saveExaminerStatus: builder.mutation<void, ExaminerCertificationStatus>({
      query: (data) => api.examinerCertificationStatus.saveExaminerStatus(data),
      invalidatesTags: ['ExaminerStatus'],
    }),

    getDisciplinaryReviewDate: builder.query<string | null, number>({
      query: (examinerId: number) =>
        api.disciplinary.getDisciplinaryReviewDate({
          examinerId,
        } as GetDisciplinaryReviewDateParams),
      providesTags: ['Examiner'],
    }),
  }),
});

export const {
  useGetExaminerListRecordsQuery,
  useSearchExaminersQuery,
  useGetExaminerQuery,
  useCreateExaminerMutation,
  useUpdateExaminerMutation,
  useLazyCheckDuplicateExaminersQuery,
  useGetExaminerStatusQuery,
  useGetTrainersQuery,
  useHasRequiredTrainingForOdpQuery,
  useSaveExaminerStatusMutation,
  useGetDisciplinaryReviewDateQuery,
} = examinersApi;
